import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/auth-context';
import { bodyMuted, btnPrimary, chip, chipActive, chipIdle, pageBg, sectionTitle, wrap } from '@/components/ui/design';
import OrganizationsTab from '@/components/admin/OrganizationsTab';
import UsersTab from '@/components/admin/UsersTab';
import EventsTab from '@/components/admin/EventsTab';
import ReportTab from '@/components/admin/ReportTab';
import CacheTab from '@/components/admin/CacheTab';
import AuditLogTab from '@/components/admin/AuditLogTab';
import ActivityTab from '@/components/admin/ActivityTab';

const TABS = [
    { key: 'organizations', label: 'Organizations', Component: OrganizationsTab },
    { key: 'users', label: 'Users', Component: UsersTab },
    { key: 'events', label: 'Events', Component: EventsTab },
    { key: 'report', label: 'Report', Component: ReportTab },
    { key: 'activity', label: 'Activity', Component: ActivityTab },
    { key: 'audit', label: 'Audit log', Component: AuditLogTab },
    { key: 'cache', label: 'Cache', Component: CacheTab },
] as const;

export const isAdminUser = (user?: { permissions?: string[] } | null) => Boolean(user?.permissions?.includes('ADMIN'));

export default function AdminPanel() {
    const { user, isLoading } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get('tab');
    const tab = TABS.find(t => t.key === requested)?.key ?? TABS[0].key;
    const setTab = (key: string) => setSearchParams({ tab: key }, { replace: true });
    const Active = TABS.find(t => t.key === tab)!.Component;

    return (
        <div className={`min-h-screen ${pageBg}`}>
            <Header />
            <main className={`${wrap} py-8 sm:py-12`}>
                {isLoading ? (
                    <p className={bodyMuted}>Loading...</p>
                ) : !isAdminUser(user) ? (
                    <div className="flex flex-col items-center gap-4 py-24 text-center">
                        <h1 className={sectionTitle}>Admins only</h1>
                        <p className={bodyMuted}>You need the admin permission to open this page.</p>
                        <button type="button" className={btnPrimary} onClick={() => navigate('/')}>Back to home</button>
                    </div>
                ) : (
                    <>
                        <h1 className={`${sectionTitle} mb-6`}>Admin panel</h1>
                        <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Admin sections">
                            {TABS.map(t => (
                                <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} className={`${chip} ${tab === t.key ? chipActive : chipIdle}`}>{t.label}</button>
                            ))}
                        </div>
                        <Active />
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}
